import React, { useState, useEffect } from "react";
import "./dstyle.css";
import SideBar from "./SideBar";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faChalkboardUser,
  faHandshake,
  faFilter,
  faEnvelope,
  faPhone,
  faBuilding,
  faGraduationCap,
  faBullseye,
  faUserPlus
} from "@fortawesome/free-solid-svg-icons";

function DRegistrations() {
  const navigate = useNavigate();
  const adminId = localStorage.getItem("adminId");
  const adminRole = localStorage.getItem("adminRole");
  const isMain = adminRole === "main";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminCreds, setAdminCreds] = useState({ email: "", password: "", username: "" });

  useEffect(() => {
    if (!adminId) { navigate("/admin-login"); return; }
    fetchRequests();
  }, [filterType, filterStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/registrations?`;
      if (filterType) url += `type=${filterType}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      const res = await fetch(url);
      const data = await res.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching registrations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (req) => {
    setSelectedRequest(req);
    setAdminCreds({ email: req.email, password: "", username: req.name });
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!adminCreds.password || adminCreds.password.length < 4) {
      alert("Password must be at least 4 characters.");
      return;
    }
    setActionLoading(selectedRequest.id);
    try {
      // Approve the request
      await fetch(`${API_BASE_URL}/api/registrations/${selectedRequest.id}/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewedBy: adminId }),
      });

      // Create sub-admin account
      await fetch(`${API_BASE_URL}/api/admins/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: adminCreds.username,
          email: adminCreds.email,
          password: adminCreds.password,
          createdBy: adminId,
        }),
      });

      setShowApproveModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error("Error approving request:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this request?")) return;
    setActionLoading(id);
    try {
      await fetch(`${API_BASE_URL}/api/registrations/${id}/reject`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewedBy: adminId }),
      });
      fetchRequests();
    } catch (err) {
      console.error("Error rejecting request:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: { background: "#fef3c7", color: "#92400e" },
      approved: { background: "#d1fae5", color: "#065f46" },
      rejected: { background: "#fee2e2", color: "#991b1b" },
    };
    return (
      <span style={{
        ...styles[status],
        padding: "4px 14px", borderRadius: "20px", fontWeight: 700, fontSize: "0.8rem",
        textTransform: "capitalize"
      }}>
        {status}
      </span>
    );
  };

  const typeBadge = (type) => (
    <span style={{
      background: type === "instructor" ? "#e0f2fe" : "#ede9fe",
      color: type === "instructor" ? "#0369a1" : "#6d28d9",
      padding: "4px 12px", borderRadius: "20px", fontWeight: 700, fontSize: "0.8rem",
      display: "inline-flex", alignItems: "center", gap: "4px"
    }}>
      <FontAwesomeIcon icon={type === "instructor" ? faChalkboardUser : faHandshake} />
      {type === "instructor" ? "Instructor" : "Partner"}
    </span>
  );

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"registrations"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px" }}>
          {/* Header */}
          <div style={{ marginBottom: "28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                <FontAwesomeIcon icon={faUserPlus} style={{ color: "#6366f1", marginRight: "10px" }} />
                Registration Requests
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.92rem" }}>
                Review and manage instructor & partner applications
              </p>
            </div>
          </div>

          {/* Filters */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: "8px 16px", border: "1px solid #e2e8f0", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600, background: "#fff", cursor: "pointer" }}>
              <option value="">All Types</option>
              <option value="instructor">Instructors</option>
              <option value="partner">Partners</option>
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: "8px 16px", border: "1px solid #e2e8f0", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600, background: "#fff", cursor: "pointer" }}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Table */}
          <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)" }}>
            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", color: "#6366f1", marginBottom: "10px" }} />
                  <p>Loading requests...</p>
                </div>
              ) : requests.length === 0 ? (
                <p style={{ padding: "40px", color: "#94a3b8", textAlign: "center" }}>No registration requests found.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.88rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontWeight: 700 }}>
                      <th style={{ padding: "14px 20px" }}>Name</th>
                      <th style={{ padding: "14px 20px" }}>Type</th>
                      <th style={{ padding: "14px 20px" }}>Email</th>
                      <th style={{ padding: "14px 20px" }}>Details</th>
                      <th style={{ padding: "14px 20px", textAlign: "center" }}>Status</th>
                      <th style={{ padding: "14px 20px" }}>Date</th>
                      {isMain && <th style={{ padding: "14px 20px", textAlign: "center" }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => (
                      <tr key={req.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "16px 20px", fontWeight: 700, color: "#0f172a" }}>{req.name}</td>
                        <td style={{ padding: "16px 20px" }}>{typeBadge(req.type)}</td>
                        <td style={{ padding: "16px 20px", color: "#475569" }}>{req.email}</td>
                        <td style={{ padding: "16px 20px", color: "#64748b", fontSize: "0.82rem", maxWidth: "200px" }}>
                          {req.type === "instructor" ? (
                            <>{req.qualification && <div><FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: "4px" }} />{req.qualification}</div>}
                            {req.main_domain && <div><FontAwesomeIcon icon={faBullseye} style={{ marginRight: "4px" }} />{req.main_domain}</div>}</>
                          ) : (
                            <>{req.organization_name && <div><FontAwesomeIcon icon={faBuilding} style={{ marginRight: "4px" }} />{req.organization_name}</div>}
                            {req.role && <div>{req.role}</div>}</>
                          )}
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "center" }}>{statusBadge(req.status)}</td>
                        <td style={{ padding: "16px 20px", color: "#64748b", fontSize: "0.82rem" }}>
                          {new Date(req.created_at).toLocaleDateString()}
                        </td>
                        {isMain && (
                          <td style={{ padding: "16px 20px", textAlign: "center" }}>
                            {req.status === "pending" ? (
                              <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                <button onClick={() => handleApproveClick(req)} disabled={actionLoading === req.id}
                                  style={{ background: "#d1fae5", color: "#065f46", border: "none", padding: "6px 14px", borderRadius: "8px", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}>
                                  <FontAwesomeIcon icon={faCheckCircle} /> Approve
                                </button>
                                <button onClick={() => handleReject(req.id)} disabled={actionLoading === req.id}
                                  style={{ background: "#fee2e2", color: "#991b1b", border: "none", padding: "6px 14px", borderRadius: "8px", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}>
                                  <FontAwesomeIcon icon={faTimesCircle} /> Reject
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: "#94a3b8", fontSize: "0.82rem" }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </section>

      {/* Approve Modal */}
      {showApproveModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 9999, backdropFilter: "blur(4px)"
        }} onClick={() => setShowApproveModal(false)}>
          <div style={{
            background: "#fff", borderRadius: "20px", padding: "32px", width: "100%", maxWidth: "460px",
            boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: "1.3rem", marginBottom: "6px" }}>
              Approve & Create Account
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.88rem", marginBottom: "24px" }}>
              Set login credentials for <strong>{selectedRequest?.name}</strong>
            </p>

            <div className="form-group">
              <label>Username</label>
              <input type="text" value={adminCreds.username}
                onChange={(e) => setAdminCreds({ ...adminCreds, username: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={adminCreds.email}
                onChange={(e) => setAdminCreds({ ...adminCreds, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Password *</label>
              <input type="text" placeholder="Set initial password" value={adminCreds.password}
                onChange={(e) => setAdminCreds({ ...adminCreds, password: e.target.value })} />
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
              <button onClick={handleApproveConfirm} disabled={actionLoading}
                style={{
                  flex: 1, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff",
                  border: "none", padding: "12px", borderRadius: "12px", fontWeight: 700, cursor: "pointer"
                }}>
                {actionLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Approve & Create Account"}
              </button>
              <button onClick={() => setShowApproveModal(false)}
                style={{ padding: "12px 20px", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#fff", fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DRegistrations;
